import { CompetenceList } from "core/blueprint/competencelist/CompetenceListService";
import { Widget } from "core/blueprint/dashboard/DashboardTypes";
import {
	Role,
	UserRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import { Field } from "core/field/FieldService";
import { StatusType } from "document/StatusTypes";
import { ScoreResolution } from "evaluation/ScoreResolution";
import { Operation } from "rfc6902";
import { checkStatus, del, get, patch, post, put } from "utils/Service";

export enum CriteriaType {
	CLUSTER = "cluster",
	COMPETENCE = "competence"
}

export type Property = {
	[key: string]: string;
};

export type Properties = {
	[date: string]: Property;
};

export interface Criteria {
	type: CriteriaType;
	name: string;
	labels: Array<string>;
	info: string;
	properties: Properties;
	latestProperties: Properties | null;
}

export interface Cluster extends Criteria {
	subsections: Criteria[];
}

export interface Score {
	score: number;
	comment?: string;
	date?: string;
}

export interface ScoreEntry {
	[date: string]: Score;
}

export interface Competence extends Criteria {
	scoreHistory: ScoreEntry;
	latestScore: ScoreEntry | null;
	loc?: number;
}

export interface Evaluation {
	minScore: number;
	maxScore: number;
	scoreNames: string[];
	scoreColours: string[];
	scoreInfo: string[];
	// Stamped from the trajectory by the server, like the scale fields above.
	scoreResolution: ScoreResolution;
	criteria: Criteria[];
}

export enum TrajectoryRight {
	READ = "READ",
	PURCHASE = "PURCHASE",
	METADATA = "METADATA",
	RIGHTS = "RIGHTS"
}

export type TrajectoryInfo = {
	id: number;
	name: string;
	description: string;
	blueprintId: number | null;
	blueprintVersion: number | null;
	rights: TrajectoryRight[];

	activeFrom: string | null; // ISO
	activeUntil: string | null; // ISO

	status: StatusType;
	statusModifiedOn: string;
	statusModifiedBy: number;
	statusModifiedByEmail: string;
};

export type UpdateBlueprintInfo = {
	name: string;
	description: string;
};

export type FormInstance = {
	fields: Field[];
};

export type TrajectoryAccess = {
	roles: Role[];
	users: UserRights[];
};

// TODO: more information.
export type ReportInfo = {
	id: number;
	name: string;
	created: string;
};

export type EvaluationInfo = {
	created: string;
};

export type CreateTrajectoryInfo = {
	name: string;
	description: string;
	blueprintId: number;
	access: TrajectoryAccess;
};

export type Grouped<T> = {
	grouped: Group<T>;
	ungrouped: Array<T>;
};

export type Group<T> = {
	[key: string]: T[];
};

const ENDPOINT = "/api/v2/trajectories";

const DASHBOARD_SUFFIX = "dashboard";
const FORM_SUFFIX = "form";
const COMPETENCE_LIST_SUFFIX = "competencelist";
const RATING_CRITERIA_SUFFIX = "ratingcriteria";
const REPORTS_SUFFIX = "reports";
const EVALUATIONS_SUFFIX = "evaluations";
const ACCESS_SUFFIX = "access";

export function createTrajectory(info: CreateTrajectoryInfo): Promise<string> {
	return post(ENDPOINT, info).then(response => {
		if (response.status !== 201) {
			throw new Error(response.statusText);
		}

		const location = response.headers.get("location");
		if (location === null) {
			throw new Error("Missing location header!");
		} else {
			return location;
		}
	});
}

export function getTrajectories(): Promise<TrajectoryInfo[]> {
	return get(ENDPOINT).then(response => response.json());
}

export function getGroupedTrajectories(
	key?: string | null
): Promise<Grouped<TrajectoryInfo>> {
	const url = `${ENDPOINT}/grouped` + (key ? `?key=${key}` : "");
	return get(url).then(response => response.json());
}

export function getTrajectoryInfo(id: number): Promise<TrajectoryInfo> {
	return get(`${ENDPOINT}/${id}`).then(response => response.json());
}

export function getTrajectoryDashboard(id: number): Promise<Array<Widget>> {
	return getTrajectoryData(id, DASHBOARD_SUFFIX).then(response =>
		response.json()
	);
}

export function getTrajectoryForm(id: number): Promise<Field[]> {
	return getTrajectoryData(id, FORM_SUFFIX).then(response => response.json());
}

export function getTrajectoryCompetenceList(
	id: number
): Promise<CompetenceList> {
	return getTrajectoryData(id, COMPETENCE_LIST_SUFFIX).then(response =>
		response.json()
	);
}

export function getTrajectoryRatingCriteria(id: number): Promise<FormInstance> {
	return getTrajectoryData(id, RATING_CRITERIA_SUFFIX).then(response =>
		response.json()
	);
}

export function getTrajectoryReports(id: number): Promise<ReportInfo[]> {
	return getTrajectoryData(id, REPORTS_SUFFIX).then(response =>
		response.json()
	);
}

// TODO: split into different parts.
export function getTrajectoryEvaluations(id: number): Promise<Evaluation> {
	return getTrajectoryData(id, EVALUATIONS_SUFFIX).then(response =>
		response.json()
	);
}

export function getTrajectoryAccess(id: number): Promise<TrajectoryAccess> {
	return getTrajectoryData(id, ACCESS_SUFFIX).then(response =>
		response.json()
	);
}

function getTrajectoryData(id: number, suffix: string): Promise<Response> {
	return get(`${ENDPOINT}/${id}/${suffix}`);
}

export function updateTrajectoryInfo(
	id: number,
	info: UpdateBlueprintInfo
): Promise<void> {
	return put(`${ENDPOINT}/${id}`, info).then(response =>
		checkStatus(response, 200)
	);
}

export function updateTrajectoryForm(
	id: number,
	patches: Operation[]
): Promise<void> {
	return patch(`${ENDPOINT}/${id}/${FORM_SUFFIX}`, patches).then(response =>
		checkStatus(response, 200)
	);
}

export type InformationDto = {
	[key: string]: string;
};

export function updateTrajectoryInformation(
	id: number,
	fields: InformationDto
): Promise<void> {
	return patch(`${ENDPOINT}/${id}/information`, fields).then(response =>
		checkStatus(response, 200)
	);
}

export function updateTrajectoryReport(
	trajectoryId: number,
	reportId: number,
	patches: Operation[]
): Promise<void> {
	return patch(
		`${ENDPOINT}/${trajectoryId}/${REPORTS_SUFFIX}/${reportId}`,
		patches
	).then(response => checkStatus(response, 200));
}

export function updateTrajectoryEvaluation(
	trajectoryId: number,
	evaluation: Evaluation
): Promise<void> {
	return put(
		`${ENDPOINT}/${trajectoryId}/${EVALUATIONS_SUFFIX}`,
		evaluation
	).then(response => checkStatus(response, 200));
}

export function updateTrajectoryAccess(
	id: number,
	access: TrajectoryAccess
): Promise<void> {
	return put(`${ENDPOINT}/${id}/${ACCESS_SUFFIX}`, access).then(response =>
		checkStatus(response, 200)
	);
}

export function deleteTrajectoryReport(
	trajectoryId: number,
	reportId: number
): Promise<void> {
	return del(
		`${ENDPOINT}/${trajectoryId}/${REPORTS_SUFFIX}/${reportId}`
	).then(response => checkStatus(response, 200));
}

export function updateBlueprintVersion(trajectoryId: number): Promise<void> {
	return put(`${ENDPOINT}/${trajectoryId}/update`, {}).then(response =>
		checkStatus(response, 200)
	);
}
